import React, { useEffect, useState, useRef } from 'react'
import Container from '../Main/Container'
import Concept from '../Concept/Concept'
import { connect } from 'react-redux'
import Title from '../Title/Title'
import {drawLineChart, kvalNotation, path} from '../Charts/D3/drawLineChart'
import { AxisX, AxisY } from '../Charts/D3/elements/chart/Axes'
import { drawSubwaySystem, drawSubwayLogos, projection, translateAlong } from '../Charts/D3/drawSubwayPaths'
import { easeLinear, selectAll, select, scaleLinear, min, max } from 'd3'
import _ from 'underscore'
import './Subway.scss'

const Subway = props => {
    const w = 1400, h = 1375
    const [trail, setTrail] = useState([])
    const [track, setTrack] = useState(1)
    const tick  = (point,b) => {
        setTrail(prevTrail => {
            if(prevTrail.length !== 0 && !prevTrail[prevTrail.length - 1].properties.line.includes(b)) {
                return [].concat(point)
            }
            return prevTrail.concat(point) }
        )
    }
    const stationsRef = useRef(null)
    const phantomClass = d => `route phantom-${ d.name }`
    const pathClass = d => `route line-${ d.properties.rt_symbol }`
    const { linesDispatch, citiesDispatch, locationsDispatch, dispatch } = props,
        { phantomLines, subwayLines } = linesDispatch,
        { nyc } = citiesDispatch,
        { features } = locationsDispatch
    const transition = evt => {
        if (evt.target.className){
            let [ a, b ] = evt.target.className.baseVal
            setTrack(b)
        }
        const ref = stationsRef.current
        if (!ref) {
            return
        }
    }
    useEffect(() => {
        dispatch.lines.loadPhantomPaths()
        dispatch.lines.loadSubwayPaths()
        dispatch.cities.loadBoroughBackgrounds()
        dispatch.locations.loadSubwayStations()
    },[])
    useEffect(() => {
       if (!Array.isArray(phantomLines.routes)) {
           const stations = features.features,
               //     duration = ref.state.duration,
               routePath = selectAll(`.phantom-${track}`),
               filter = _.filter( stations, o => o.properties.line.includes(track) )
           const tween = translateAlong(routePath, filter, projection, tick, track)
           selectAll('.point')
               .transition()
               .delay(500)
               .ease(easeLinear)
               .duration(200000)
               .attrTween('transform', tween )
       }
    },[track])
    const [a, ...rest] = 'click on Subway button'
    return ( !Array.isArray(phantomLines.routes) && !Array.isArray(subwayLines.routes) &&
            !Array.isArray(nyc) &&
        <div className='main-page'>
            <Title h1='A Socioeconomic ride through the NYC Subway'/>
            <Concept selection='click-line' style={{ gridColumn : '1/2' }}>
                <div className={ ['div-buttons', 'selected' ].join(' ') }>
                    <button className={ ['buttons','selected', 'button-loc'].join(' ') }>
                        { `${ a.toUpperCase() + rest .join('')}` }
                    </button>
                </div>
                <Container
                    onClick={ transition }
                    id='logo-container'
                    data={['1','6','L']}
                    width={ w / 2 }
                    height={ 50 }
                    renderChart={ drawSubwayLogos }/>
            </Concept>
            <Container
                id='borough-container'
                data={[
                    nyc,
                    { ...phantomLines, pathClass: phantomClass, geographyKey: 'routes'},
                    { ...subwayLines, pathClass, geographyKey: 'features' },
                    features,
                    trail
                ]}
                width={ w }
                height={ h }
                renderChart={ drawSubwaySystem }
            >
                <g transform='translate(50,50)' onClick={transition}/>
                <g className='g-subway-paths'/>
                <g className='g-phantom-paths'/>
                <g className='g-boroughs-background'/>
                <g className='g-point'/>
                <g className='g-subway-stations' ref={ stationsRef }/>
                <IncomeGraph dataset={trail} ref={ stationsRef }/>
            </Container>
        </div>
    )
}

const IncomeGraph = props => {
    const gRef = useRef(null)
    const { dataset } = props
    const w = 1400, h = 1375
    const margin = { top: 35, left: 35, bottom: 0, right:0 }
    const dimensions = { height: 400 - margin.top - margin.bottom, width: w - margin.left - margin.right }
    useEffect(() => {
        if (!gRef.current) {
            return
        }
        select(gRef.current)
            .append('path')
            .attr('class', 'income-line')
    },[])
    useEffect(() => {
        let
            g = select('.g-income-graph'),
            yDomain = [0, 260000],
            uniq = _.uniq(dataset, o => o.properties.name),
            yScale = scaleLinear().range([h / 2, 0]).domain(yDomain), // Define yScale
            xScale = scaleLinear().range([0, w / 3]), // Define xScale,
            circle = g.selectAll('circle').data(uniq),
            text = g.selectAll('.neigh-text').data(uniq)

        path.x(d => xScale(d.properties.ts)).y(d => yScale(d.properties.dp03_0062e)), // Define xScale and yScale based on your setup.
        xScale.domain([min(dataset, d => d.properties.ts), max(dataset, d => d.properties.ts)])
        // Adjust y-domain based on your data
        g.select('.income-line')
            .datum(uniq)
            .attr('d', path(uniq)) // Pass the unique data to the line generator
            .style('fill', 'none')
            .style('stroke', '#e74c3c')
            .style('stroke-width', 2)
            .attr('transform', `translate(50,10)`)

        circle.exit().remove()
        circle.enter().append('circle')
        circle
            .attr('class', 'station-circle')
            .attr('cx', d => xScale(d.properties.ts))
            .attr('cy', d => yScale(d.properties.dp03_0062e))
            .attr('r', 3)
            .style('stroke-width', 1)
            .style('stroke','red')
            .style('fill', 'white')
            .attr('transform', `translate(50,10)`)

        text.exit().remove()
        text.enter().append('text').attr('class', 'neigh-text')
        text
            .attr('x', d => xScale(d.properties.ts) )
            .attr('y', d => yScale(d.properties.dp03_0062e))
            .style('text-shadow', '0 0 1px darkgrey, 0 0 2px lightgrey, 0 0 4px grey, 0 0 5px white')
            .html(d => {
                const x = xScale(d.properties.ts)
                return `<tspan style="font-weight:bold;" dy="-10" x={x}>${ d.properties.name }
</tspan><tspan dy="12" x=${x}>$${ kvalNotation(d.properties.dp03_0062e) }</tspan>`
            })
    },[dataset])
    return (<g className='g-income-graph' { ...dimensions } ref={ gRef }>
                <AxisX
                    x={ w / 3 }
                    y={ -6 }
                    text='Median Household Income By Subway Station Neighborhood'
                    fontSize={ 2 }
                    position={ `translate(50,${ (h / 2) + 10 })`}
                />
                <AxisY
                    x={ w }
                    y= { -6 }
                    dy='.71em'
                    text='test me'
                    position={ 'translate(50,10)'}
                    transformText='rotate(-90)'
                />
            </g>)
}

const mapStateToProps = () => (
    dispatch => ({
        linesDispatch: dispatch.lines,
        citiesDispatch: dispatch.cities,
        locationsDispatch: dispatch.locations
    })
)

export default connect(mapStateToProps)(Subway)
